import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import ChatMessage from './components/ChatMessage';
import StreamingResponse from './components/StreamingResponse';
import Dashboard from './components/Dashboard';
import TenantHeader from './components/TenantHeader';
import FormCanvas from './components/forms/FormCanvas'; // FormCanvas 추가
import FormTabs from './components/forms/FormTabs'; // FormTabs 추가
import ResizablePanel from './components/ResizablePanel'; // 새 패널 시스템
import FloatingTabNav from './components/FloatingTabNav'; // 플로팅 탭 네비게이션
import PasswordPromptModal from './components/PasswordPromptModal'; // 비밀번호 입력 모달 추가
import ImageModal from './components/ImageModal'; // 이미지 모달 추가
import { DashboardProvider } from './hooks/useDashboardContext';
import { usePanelLayout, FORCE_HIDE_FORMS_PANEL } from './hooks/usePanelLayout'; // 패널 레이아웃 훅
import { formatMarkdownLike } from './utils/markdownFormatter'; // 마크다운 포맷터 import
import { EmergencyProvider } from './contexts/EmergencyContext';
import EmergencyOverlay from './components/EmergencyOverlay';
import EmergencyStatusBanner from './components/EmergencyStatusBanner';
import { DashboardPopupProvider } from './contexts/DashboardPopupContext';
import DashboardPopupManager from './components/DashboardPopupManager';
import { GlobalVariablesProvider } from './contexts/GlobalVariablesContext';
import GlobalVariablesDebugPanel from './components/GlobalVariablesDebugPanel';
// 스트리밍 서비스 import 추가
import { createStreamingService } from './services/streamingService';
// 세션 관리 서비스 import 추가
import { createChatSessionManager } from './services/chatSessionService';
// 모델 설정 import 추가
import { getCurrentModel } from './config/modelConfig';
// API 기본 URL 상수 정의 - 환경 변수 사용
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://backend:8000/api'; 

// 프록시 설정을 통해 요청이 백엔드로 전달되도록 상대 경로 사용
const API_BASE_URL = '/api';

// Docker 내부 백엔드 주소 직접 사용
// const API_BASE_URL = 'http://backend:8000/api'; // 주석 처리

// 다른 URL 옵션 (참고용, 사용 X)
// const API_BASE_URL = 'http://localhost:8000/api';
// const API_BASE_URL = 'http://backend:8000/api'; // Docker 컨테이너 간 통신용 URL

// React Grid Layout의 passive 이벤트 리스너 경고 해결을 위한 패치
// 컴포넌트 외부에서 한 번만 실행되도록 처리
(() => {
  if (typeof EventTarget !== 'undefined' && typeof window !== 'undefined') {
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    const touchEvents = ['touchstart', 'touchmove', 'wheel', 'mousewheel'];

    EventTarget.prototype.addEventListener = function(type, listener, options) {
      // 터치 및 스크롤 이벤트를 passive로 설정
      if (touchEvents.includes(type)) {
        if (typeof options === 'object') {
          options = { ...options, passive: true };
        } else if (options === undefined || options === false || options === true) {
          options = { passive: true, capture: Boolean(options) };
        }
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
  }
})();

function App() {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 개발자 모드 상태 추가 - 기본값은 항상 off (false)
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);

  // 이미지 업로드 관련 상태 추가
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // Max Intelligence 모드 상태 추가 (기본값 true로 변경)
  const [isMaxIntelligence, setIsMaxIntelligence] = useState(true);
  
  // 이미지 모달 상태 추가
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  // 개발자 모드 비밀번호 모달 상태 추가
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  
  // 전체화면 상태 관리 추가
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // 패널 레이아웃 관리 훅
  const {
    panelConfig,
    isDragging,
    togglePanel,
    resizePanel,
    startDrag,
    endDrag,
    resetToDefault
  } = usePanelLayout();
  
  // FormCanvas 상태 추가
  const [isFormCanvasOpen, setIsFormCanvasOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  
  // 로그인 상태 확인
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  
  // 헤더 타이틀을 위한 사용자, 테넌트, 대시보드 정보 상태 추가
  const [headerUserInfo, setHeaderUserInfo] = useState(null);
  const [headerCurrentTenant, setHeaderCurrentTenant] = useState('');
  const [currentDashboardTitle, setCurrentDashboardTitle] = useState('');
  
  // TenantHeader 콜백 함수들 - useCallback으로 메모이제이션
  const handleUserInfoChange = useCallback((userInfo) => {
    setHeaderUserInfo(userInfo);
  }, []);
  
  const handleTenantChange = useCallback((tenantId) => {
    setHeaderCurrentTenant(tenantId);
  }, []);

  // 대시보드 제목 업데이트 함수 추가
  const handleDashboardTitleChange = useCallback((title) => {
    setCurrentDashboardTitle(title);
  }, []);

  // 대시보드 컨텍스트에서 대시보드 템플릿 변경 감지
  // const { dashboardTemplate } = useDashboardContext(); // 제거
  
  // 헤더 타이틀 동적 생성 함수 - 대시보드 제목을 메인으로 표시
  const generateHeaderTitle = () => {
    // 우선순위: 1) 대시보드 제목, 2) 테넌트-사용자, 3) 기본값
    if (currentDashboardTitle) {
      return currentDashboardTitle;
    }
    
    if (!headerUserInfo && !headerCurrentTenant) {
      return 'EnerWiser 분석 서비스'; // 기본값
    }
    
    const username = headerUserInfo?.username || '';
    const tenantDisplay = headerCurrentTenant === 'ess' ? 'ESS' : 
                         headerCurrentTenant === 'evcheck' ? 'EVCheck' : 
                         headerCurrentTenant;
    
    if (username && tenantDisplay) {
      return `${tenantDisplay} - ${username}`;
    } else if (username) {
      return `EnerWiser - ${username}`;
    } else if (tenantDisplay) {
      return `${tenantDisplay} - EnerWiser`;
    }
    
    return 'EnerWiser 분석 서비스';
  };

  // 서브타이틀 생성 함수 - 테넌트와 사용자 정보를 서브로 표시
  const generateSubTitle = () => {
    if (!currentDashboardTitle) {
      return null; // 대시보드 제목이 없으면 서브타이틀도 없음
    }

    const username = headerUserInfo?.username || '';
    const tenantDisplay = headerCurrentTenant === 'ess' ? 'ESS' : 
                         headerCurrentTenant === 'evcheck' ? 'EVCheck' : 
                         headerCurrentTenant;
    
    if (username && tenantDisplay) {
      return `${tenantDisplay} • ${username}`;
    } else if (tenantDisplay) {
      return tenantDisplay;
    } else if (username) {
      return username;
    }
    
    return null;
  };
  
  // 채팅 모드 관련 상태 - 항상 'session'모드로 고정
  const [sessionId, setSessionId] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  
  // 스트리밍 텍스트 상태 (최종 메시지 저장용)
  const [streamingText, setStreamingText] = useState('');
  
  // 최종 메시지에 저장될 시각화 데이터 상태
  const [currentVisualizations, setCurrentVisualizations] = useState([]);
  const visualizationsRef = useRef([]);
  
  // 스트리밍 중 UI 표시를 위한 콘텐츠 블록 상태
  const [streamingDisplayBlocks, setStreamingDisplayBlocks] = useState([]);
  
  // 스트리밍 서비스 ref 추가
  const streamingServiceRef = useRef(null);
  
  // 중단 관련 상태 추가
  const [isCancelling, setIsCancelling] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState(null);
  
  // 자동 스크롤을 위한 참조 추가
  const messagesEndRef = useRef(null);
  const chatAreaRef = useRef(null); // 채팅 영역 참조 추가
  
  // 스크롤 관련 상태 추가
  const [isAtBottom, setIsAtBottom] = useState(true); // 사용자가 하단에 있는지 여부
  const [userScrolled, setUserScrolled] = useState(false); // 사용자가 수동 스크롤했는지 여부
  
  // 전체화면 관련 함수들 추가
  const enterFullscreen = async () => {
    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        await document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement.msRequestFullscreen) {
        await document.documentElement.msRequestFullscreen();
      }
    } catch (error) {
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch (error) {
    }
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  // 전체화면 상태 감지 (F11 포함)
  const checkFullscreenStatus = () => {
    // JavaScript Fullscreen API 확인
    const apiFullscreen = !!(document.fullscreenElement || 
                            document.webkitFullscreenElement || 
                            document.msFullscreenElement);
    
    // F11 전체화면 감지 (브라우저 UI가 없고 화면 크기가 최대일 때)
    const browserFullscreen = window.innerHeight === window.screen.height && 
                             window.innerWidth === window.screen.width;
    
    return apiFullscreen || browserFullscreen;
  };

  // 전체화면 상태 변화 감지
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(checkFullscreenStatus());
    };

    const handleResize = () => {
      setIsFullscreen(checkFullscreenStatus());
    };

    // 초기 상태 설정
    setIsFullscreen(checkFullscreenStatus());

    // 이벤트 리스너 등록 (passive 옵션 추가)
    document.addEventListener('fullscreenchange', handleFullscreenChange, { passive: true });
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange, { passive: true });
    document.addEventListener('msfullscreenchange', handleFullscreenChange, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  
  // 스크롤 위치 감지 함수
  const checkIfAtBottom = useCallback(() => {
    if (!chatAreaRef.current) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
    const threshold = 50; // 하단에서 50px 이내면 "하단"으로 간주
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= threshold;
    
    return isNearBottom;
  }, []);

  // 스크롤 이벤트 핸들러
  const handleScroll = useCallback(() => {
    const atBottom = checkIfAtBottom();
    setIsAtBottom(atBottom);
    
    // 사용자가 수동으로 스크롤했음을 표시
    if (!atBottom) {
      setUserScrolled(true);
    } else {
      // 다시 하단으로 스크롤하면 자동 스크롤 재개
      setUserScrolled(false);
    }
  }, [checkIfAtBottom]);

  // 스크롤 함수 수정 - 조건부 자동 스크롤
  const scrollToBottom = useCallback(() => {
    // 사용자가 하단에 있을 때만 자동 스크롤
    if (isAtBottom && !userScrolled) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isAtBottom, userScrolled]);
  
  // 스크롤 이벤트 리스너 등록
  useEffect(() => {
    const chatArea = chatAreaRef.current;
    if (chatArea) {
      chatArea.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        chatArea.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);
  
  // 스트리밍 텍스트 또는 채팅 기록, 스트리밍 블록이 변경될 때마다 조건부 스크롤
  useEffect(() => {
    scrollToBottom();
    // 의존성 배열에 streamingDisplayBlocks 추가
  }, [streamingText, chatHistory, streamingDisplayBlocks, scrollToBottom]);
  
  // 스트리밍이 완료될 때 대화 기록을 업데이트하는 부분을 수정
  useEffect(() => {
    if (!loading && streamingText) {
      // 로그 제거 (상시 출력 방지)
    }
  }, [loading, streamingText]);
  
  // 서버 URL 설정
  // 백엔드에 존재하는 엔드포인트만 정의하도록 정리함
  const STREAM_SESSION_URL = `${API_BASE_URL}/chat/sessions/stream/`;
  
  // 초기 안내 메시지
  const initialGreeting = `EnerWiser 분석 서비스에 오신 것을 환영합니다.
궁금한 점을 물어보세요!!`;
  
  // 초기 메시지 객체 (explanation 필드 추가)
  const initialMessage = {
    role: 'assistant',
    content: initialGreeting,
    explanation: "EnerWiser 서비스 소개 및 가능한 질문 예시를 안내합니다.",
    query: ""
  };
  
  // 세션 매니저 초기화
  const sessionManagerRef = useRef(null);
  
  // 세션 매니저 콜백 함수들 정의
  const sessionCallbacks = {
    onLoadingChange: setLoading,
    onErrorChange: setError,
    onVisualizationsChange: setCurrentVisualizations,
    onSessionIdChange: setSessionId,
    onChatHistoryChange: setChatHistory,
    onSessionsUpdate: setSessions,
    getCurrentSessions: () => sessions
  };

  // 세션 매니저 인스턴스 생성 (한 번만 실행)
  if (!sessionManagerRef.current) {
    sessionManagerRef.current = createChatSessionManager({
      apiBaseUrl: API_BASE_URL,
      streamSessionUrl: STREAM_SESSION_URL,
      callbacks: sessionCallbacks,
      initialMessage: initialMessage
    });
  }

  // 컴포넌트 마운트 시 초기 메시지 설정 - 의존성 배열 수정하여 한 번만 실행되도록
  useEffect(() => {
    setChatHistory([initialMessage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 한 번만 실행되도록 빈 배열 유지
  
  // 초기 세션 목록 로드 (실제로는 인증이 필요하지만 테스트 목적으로 주석 처리)
  /*
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/chat/sessions/`);
        if (res.ok) {
          const data = await res.json();
          setSessions(data);
        }
      } catch (error) {
      }
    };
    
    fetchSessions();
  }, []);
  */
  
  // 새 세션 생성 함수 (세션 매니저 사용)
  const createNewSession = () => {
    return sessionManagerRef.current?.createSession();
  };
  
  // 폼 제출 핸들러 - 스트리밍 서비스 및 세션 매니저 사용
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() && uploadedImages.length === 0) return;
    
    // 사용자 메시지 저장 및 입력창 즉시 초기화
    const userMessage = message;
    const currentImages = [...uploadedImages]; // 현재 업로드된 이미지 복사
    setMessage(''); // 메시지 전송 즉시 입력창 비우기
    setUploadedImages([]); // 업로드된 이미지 초기화
    
    // textarea 높이 초기화
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    // 새 메시지 제출 시 자동으로 하단으로 스크롤하고 자동 스크롤 재개
    setIsAtBottom(true);
    setUserScrolled(false);
    
    setLoading(true);
    setError('');
    setIsCancelling(false); // 중단 상태 초기화
    
    // 스트리밍 응답을 위한 초기화
    setStreamingText('');
    setCurrentVisualizations([]);
    setStreamingDisplayBlocks([]);
    visualizationsRef.current = [];
    
    try {
      // 세션 매니저를 통한 세션 유효성 검사 및 생성
      const currentSessionId = await sessionManagerRef.current?.ensureValidSession(sessionId);
      if (!currentSessionId) {
        throw new Error('세션을 생성할 수 없습니다.');
      }
      
      // 사용자 메시지 즉시 표시 및 메시지 ID 저장
      const userMsgWithId = { 
        role: 'user', 
        content: userMessage, 
        id: Date.now(),
        images: currentImages // 업로드된 이미지 정보 포함
      };
      setChatHistory(prev => [...prev, userMsgWithId]);
      // setCurrentMessageId(userMsgWithId.id); // 임시 ID 사용 중단 - 실제 Claude ID를 기다림
      
      // 현재 선택된 모델 가져오기
      const currentModel = getCurrentModel(isMaxIntelligence);
      // 이미지가 있는 경우 메시지 형식 변경
      let finalMessage = userMessage;
      if (currentImages.length > 0) {
        // 각 이미지 URL을 개별적으로 [첨부된 이미지] 태그와 함께 추가
        const imageUrls = currentImages.map(img => `[첨부된 이미지]\n${img.url}`).join('\n\n');
        finalMessage = `${userMessage}\n\n${imageUrls}`;
      }
      
      // 요청 URL 생성 (세션 매니저 사용)
      const requestUrl = sessionManagerRef.current?.buildStreamingRequestUrl(currentSessionId, finalMessage, currentModel, isDeveloperMode);
      // 로그 제거 (상시 출력 방지)

      // 기존 스트리밍 서비스 정리
      if (streamingServiceRef.current) {
        streamingServiceRef.current.cleanup();
      }

      // 스트리밍 서비스 콜백 정의
      const streamingCallbacks = {
        onTextUpdate: (text) => {
          setStreamingText(text);
        },
        onVisualizationUpdate: (visualizations) => {
          setCurrentVisualizations(visualizations);
        },
        onBlocksUpdate: (blocks) => {
          if (typeof blocks === 'function') {
            setStreamingDisplayBlocks(blocks);
          } else {
            setStreamingDisplayBlocks(blocks);
          }
        },
        onError: (error) => {
          setError(error);
        },
        onComplete: (finalData) => {
          // 최종 데이터를 채팅 기록에 추가
          setChatHistory(prev => [...prev, finalData]);
          setCurrentMessageId(null); // 메시지 ID 초기화
        },
        onLoadingChange: (isLoading) => {
          setLoading(isLoading);
          if (!isLoading) {
            setCurrentMessageId(null); // 로딩 완료 시 메시지 ID 초기화
          }
        },
        onChatHistoryUpdate: (historyUpdater) => {
          setChatHistory(historyUpdater);
        },
        onCancelled: () => {
          // 중단 완료 시 상태 초기화
          setIsCancelling(false);
          setCurrentMessageId(null);
          setLoading(false);
        },
        onMessageIdReceived: (messageId) => {
          // 실제 Claude 메시지 ID를 받아서 설정
          // 로그 제거 (상시 출력 방지)
          setCurrentMessageId(messageId);
        }
      };

      // Fetch 스트리밍 서비스 생성 및 시작
      const streamingService = createStreamingService('fetch', streamingCallbacks);
      streamingServiceRef.current = streamingService;
      
      await streamingService.start(requestUrl, isDeveloperMode);
      
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setCurrentMessageId(null); // 오류 시 메시지 ID 초기화
    }
  };
  
  // 중단 함수 추가
  const handleCancel = async () => {
    // 로그 제거 (상시 출력 방지)
    
    // 조건 완화: loading 중이면 중단 가능하도록 수정
    if (!sessionId || isCancelling) {
      // 로그 제거 (상시 출력 방지)
      return;
    }
    
    // 즉시 UI 피드백 제공 - 중단 상태로 변경
    setIsCancelling(true);
    
    // currentMessageId가 없으면 임시 ID 생성
    const messageIdToUse = currentMessageId || Date.now();
    // 로그 제거 (상시 출력 방지)
    
    try {
      // 로그 제거 (상시 출력 방지)
      
      // 중단 API 호출
      const response = await fetch(`${API_BASE_URL}/chat/sessions/${sessionId}/cancel/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // 쿠키 포함
        body: JSON.stringify({
          message_id: messageIdToUse,
          reason: 'user_cancelled'
        })
      });
      
      // 로그 제거 (상시 출력 방지)
      
      if (response.ok) {
        const result = await response.json();
        // 로그 제거 (상시 출력 방지)
        
        // 스트리밍 서비스 정리를 하지 않음 - streamingService에서 자체적으로 중단 처리 완료
        // if (streamingServiceRef.current) {
        //   streamingServiceRef.current.cleanup();
        // }
        
        // 상태 초기화는 streamingService의 onComplete 콜백에서 처리됨
        // setLoading(false);
        // setCurrentMessageId(null);
        
      } else {
        const errorData = await response.json();
        setError(`중단 요청 실패: ${errorData.error || '알 수 없는 오류'}`);
        // 중단 요청 실패 시 isCancelling 상태 즉시 해제
        setIsCancelling(false);
      }
      
    } catch (err) {
      setError(`중단 요청 중 오류: ${err.message}`);
      // 오류 발생 시 isCancelling 상태 즉시 해제
      setIsCancelling(false);
    }
    // finally 블록 제거 - 성공 시에는 streamingService의 onCancelled 콜백에서 처리
    // 로그 제거 (상시 출력 방지)
  };
  
  // 스트리밍 텍스트가 변경될 때마다 디버그 로그
  useEffect(() => {
    if (streamingText && isDeveloperMode) {
      // 로그 제거 (상시 출력 방지)
    }
  }, [streamingText, isDeveloperMode]);
  
  // 페이지 로드 시 새 세션 자동 생성 - 딱 한 번만 실행되도록 조건 추가
  useEffect(() => {
    // 세션이 없고 이전에 생성 시도를 하지 않은 경우에만 생성
    if (!sessionId && !window.sessionCreationAttempted) {
      window.sessionCreationAttempted = true; // 플래그 설정
      // 로그 제거 (상시 출력 방지)
      createNewSession();
    }
  }, [sessionId]);
  

  
  // 시각화 데이터 초기화 시 ref도 함께 초기화 - 의존성 배열에 currentVisualizations 추가
  useEffect(() => {
    visualizationsRef.current = currentVisualizations;
  }, [currentVisualizations]);
  
  // 로그아웃 함수
  const handleLogout = () => {
    // 로컬 스토리지에서 토큰 제거
    localStorage.removeItem('token');
    
    // 로그인 상태 업데이트
    setIsLoggedIn(false);
    
    // 개발자 모드 비활성화
    setIsDeveloperMode(false);
    
    // 세션 초기화
    setSessionId(null);
    
    // 채팅 기록 초기화
    setChatHistory([initialMessage]);
    
    // 로그인 페이지로 즉시 리다이렉트
    window.location.href = '/login';
  };
  
  // 컴포넌트 언마운트 시 스트리밍 서비스 및 세션 매니저를 정리하는 useEffect 추가
  useEffect(() => {
    return () => {
      if (streamingServiceRef.current) {
        streamingServiceRef.current.cleanup();
      }
      if (sessionManagerRef.current) {
        sessionManagerRef.current.cleanup();
      }
    };
  }, []);
  
  // 키보드 이벤트 핸들러 추가
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift+Enter: 줄바꿈 (기본 동작 유지)
        return;
      } else {
        // Enter: 메시지 전송
        e.preventDefault();
        handleSubmit(e);
      }
    }
  };

  // textarea 높이 자동 조절 함수 추가
  const textareaRef = useRef(null);
  
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  // 메시지 변경 핸들러 수정
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  // FormCanvas 핸들러 함수들
  const handleCloseFormCanvas = () => {
    setIsFormCanvasOpen(false);
    setSelectedForm(null);
  };

    // FormTabs에서 폼 선택 시 호출되는 핸들러
  const handleFormSelect = (form) => {
    setSelectedForm(form);
    setIsFormCanvasOpen(true);
  };

  // 개발자 모드 토글 핸들러 (매번 비밀번호 요구)
  const handleDeveloperModeToggle = () => {
    if (isDeveloperMode) {
      // 현재 켜져있으면 바로 끄기
      setIsDeveloperMode(false);
    } else {
      // 현재 꺼져있으면 항상 비밀번호 모달 열기
      setIsPasswordModalOpen(true);
    }
  };

  // 개발자 모드 비밀번호 확인 핸들러 (매번 인증 요구)
  const handleDeveloperModePasswordConfirm = async (password) => {
    return new Promise((resolve, reject) => {
      if (password === '!m2k2') {
        setIsDeveloperMode(true);
        setIsPasswordModalOpen(false);
        // localStorage 저장하지 않음 - 매번 인증 요구
        // 성공 시 조용히 활성화 (alert 제거)
        resolve();
      } else {
        // 실패 시 모달 내에서 에러 메시지 표시 (alert 제거)
        reject(new Error('잘못된 비밀번호입니다.'));
      }
    });
  };

  // 개발자 모드 비밀번호 모달 닫기 핸들러
  const handleDeveloperModePasswordClose = () => {
    setIsPasswordModalOpen(false);
  };
  
  // 이미지 업로드 핸들러
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    // 업로드 개수 제한 (예: 10개)
    const maxFiles = 10;
    if (uploadedImages.length + files.length > maxFiles) {
      setError(`최대 ${maxFiles}개까지 업로드 가능합니다. 현재 ${uploadedImages.length}개 업로드됨.`);
      return;
    }
    
    // 파일 유효성 검사
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    const invalidFiles = files.filter(file => 
      !allowedTypes.includes(file.type) || file.size > maxSize
    );
    
    if (invalidFiles.length > 0) {
      const errors = invalidFiles.map(file => {
        if (!allowedTypes.includes(file.type)) {
          return `${file.name}: 지원하지 않는 파일 형식`;
        }
        if (file.size > maxSize) {
          return `${file.name}: 파일 크기 10MB 초과`;
        }
      }).join('\n');
      
      setError(`다음 파일을 업로드할 수 없습니다:\n${errors}`);
      return;
    }
    
    // 세션 확인
    const currentSessionId = await sessionManagerRef.current?.ensureValidSession(sessionId);
    if (!currentSessionId) {
      setError('세션을 생성할 수 없습니다.');
      return;
    }
    
    setIsUploadingImage(true);
    setError('');
    
    try {
      const uploadedFiles = [];
      const failedFiles = [];
      
      // 각 파일을 순차적으로 업로드
      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append('image', file);
          
          const uploadUrl = `${API_BASE_URL}/chat/sessions/${currentSessionId}/upload-image/`;
          const response = await fetch(uploadUrl, {
            method: 'POST',
            credentials: 'include',
            body: formData
          });
          
          const responseText = await response.text();
          
          if (!response.ok) {
            // HTML이 반환된 경우
            if (responseText.includes('<!doctype') || responseText.includes('<!DOCTYPE')) {
              throw new Error('서버 오류: 예상치 못한 HTML 응답');
            }
            
            // JSON 파싱 시도
            try {
              const errorData = JSON.parse(responseText);
              throw new Error(errorData.error || '이미지 업로드에 실패했습니다.');
            } catch (e) {
              throw new Error('이미지 업로드에 실패했습니다.');
            }
          }
          
          // 정상 응답도 JSON 파싱
          let data;
          try {
            data = JSON.parse(responseText);
          } catch (e) {
            throw new Error('서버 응답을 처리할 수 없습니다.');
          }
          
          uploadedFiles.push({
            url: data.image_url,
            filename: data.filename,
            size: data.size,
            contentType: data.content_type,
            file: file // 미리보기용 원본 파일 객체
          });
          
        } catch (fileError) {
          failedFiles.push({
            name: file.name,
            error: fileError.message
          });
        }
      }
      
      // 성공한 파일들을 상태에 추가
      if (uploadedFiles.length > 0) {
        setUploadedImages(prev => [...prev, ...uploadedFiles]);
      }
      
      // 실패한 파일이 있으면 에러 메시지 표시
      if (failedFiles.length > 0) {
        const errorMessage = failedFiles
          .map(f => `${f.name}: ${f.error}`)
          .join('\n');
        setError(`일부 파일 업로드 실패:\n${errorMessage}`);
      }
      
      // 파일 입력 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploadingImage(false);
    }
  };
  
  // 이미지 제거 핸들러
  const handleRemoveImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  // 클립보드 붙여넣기 핸들러
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    // 클립보드에서 이미지 찾기
    const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
    if (imageItems.length === 0) return;

    // 붙여넣기 이벤트 기본 동작 방지 (텍스트 붙여넣기 방지)
    e.preventDefault();

    // 업로드 개수 제한 체크
    const maxFiles = 10;
    if (uploadedImages.length + imageItems.length > maxFiles) {
      setError(`최대 ${maxFiles}개까지 업로드 가능합니다. 현재 ${uploadedImages.length}개 업로드됨.`);
      return;
    }

    // 세션 확인
    const currentSessionId = await sessionManagerRef.current?.ensureValidSession(sessionId);
    if (!currentSessionId) {
      setError('세션을 생성할 수 없습니다.');
      return;
    }

    setIsUploadingImage(true);
    setError('');

    const uploadedFiles = [];
    const failedFiles = [];

    // 각 이미지 아이템 처리
    for (const item of imageItems) {
      try {
        const file = item.getAsFile();
        if (!file) continue;

        // 파일 유효성 검사
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        const maxSize = 10 * 1024 * 1024; // 10MB

        if (!allowedTypes.includes(file.type)) {
          failedFiles.push({
            name: file.name || 'clipboard-image',
            error: '지원하지 않는 파일 형식'
          });
          continue;
        }

        if (file.size > maxSize) {
          failedFiles.push({
            name: file.name || 'clipboard-image',
            error: '파일 크기 10MB 초과'
          });
          continue;
        }

        // 서버에 업로드
        const formData = new FormData();
        formData.append('image', file);

        const uploadUrl = `${API_BASE_URL}/chat/sessions/${currentSessionId}/upload-image/`;
        const response = await fetch(uploadUrl, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        const responseText = await response.text();

        if (!response.ok) {
          if (responseText.includes('<!doctype') || responseText.includes('<!DOCTYPE')) {
            throw new Error('서버 오류: 예상치 못한 HTML 응답');
          }

          try {
            const errorData = JSON.parse(responseText);
            throw new Error(errorData.error || '이미지 업로드에 실패했습니다.');
          } catch (e) {
            throw new Error('이미지 업로드에 실패했습니다.');
          }
        }

        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          throw new Error('서버 응답을 처리할 수 없습니다.');
        }

        uploadedFiles.push({
          url: data.image_url,
          filename: data.filename || 'clipboard-image',
          size: data.size,
          contentType: data.content_type,
          file: file // 미리보기용 원본 파일 객체
        });

      } catch (error) {
        failedFiles.push({
          name: 'clipboard-image',
          error: error.message
        });
      }
    }

    // 성공한 파일들을 상태에 추가
    if (uploadedFiles.length > 0) {
      setUploadedImages(prev => [...prev, ...uploadedFiles]);
    }

    // 실패한 파일이 있으면 에러 메시지 표시
    if (failedFiles.length > 0) {
      const errorMessage = failedFiles
        .map(f => `${f.name}: ${f.error}`)
        .join('\n');
      setError(`일부 이미지 업로드 실패:\n${errorMessage}`);
    }

    setIsUploadingImage(false);
  }, [uploadedImages.length, sessionId, API_BASE_URL]);

  // 이미지 클릭 핸들러 (모달 열기)
  const handleImageClick = (imageData) => {
    setSelectedImage(imageData);
    setIsImageModalOpen(true);
  };

  // 이미지 모달 닫기 핸들러
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // 패널 크기 변경 시 CSS 변수 업데이트
  useEffect(() => {
    const updateCSSVariables = () => {
      const dashboardWidth = panelConfig.dashboard.visible ? panelConfig.dashboard.width : 0;
      const formsWidth = FORCE_HIDE_FORMS_PANEL ? 0 : (panelConfig.forms.visible ? panelConfig.forms.width : 0);
      const chatPanelLeftOffset = dashboardWidth + formsWidth;
      
      // 채팅창의 실제 크기는 전체 화면에서 대시보드와 폼 탭을 뺀 나머지 공간
      const viewportWidth = window.innerWidth;
      const actualChatWidth = panelConfig.chat.visible ? (viewportWidth - chatPanelLeftOffset) : 0;
      
      // FormCanvas 우측 여백 = 채팅창 너비 + 폼 탭 너비 (채팅창이 보이지 않으면 폼 탭 너비만)
      const formCanvasRightSpace = actualChatWidth + formsWidth;
      
      // SlideSearchPanel 우측 위치 = 뷰포트 너비 - 채팅창 시작 위치 + SlideSearchPanel 너비 (FormCanvas 왼쪽에 위치)
      const slidePanelRightOffset = viewportWidth - chatPanelLeftOffset + 566; 
      
      document.documentElement.style.setProperty('--chat-panel-left-offset', `${chatPanelLeftOffset}px`);
      document.documentElement.style.setProperty('--form-canvas-right-space', `${formCanvasRightSpace}px`);
      document.documentElement.style.setProperty('--slide-panel-right-offset', `${slidePanelRightOffset}px`);
      
    };

    updateCSSVariables();
    
    // 윈도우 리사이즈 시에도 업데이트
    const handleResize = () => {
      updateCSSVariables();
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [panelConfig]);

  return (
    <DashboardProvider>
      <EmergencyProvider>
        <GlobalVariablesProvider>
          <DashboardPopupProvider>
            <div className={`app-container ${isDragging ? 'dragging' : ''}`}>

        
        {/* 헤더 - 테넌트 선택기 */}
        <div className="app-header">
          <div className="app-title-section">
            <h1 className="app-title">{generateHeaderTitle()}</h1>
            {generateSubTitle() && (
              <span className="app-subtitle">{generateSubTitle()}</span>
            )}
          </div>
          <div className="header-controls">
            <button 
              className="fullscreen-toggle-button"
              onClick={toggleFullscreen}
              title={isFullscreen ? "전체화면 종료" : "전체화면 진입"}
            >
              {isFullscreen ? "🗗" : "⛶"}
            </button>
            <TenantHeader 
              onUserInfoChange={handleUserInfoChange}
              onTenantChange={handleTenantChange}
              onDashboardTitleChange={handleDashboardTitleChange}
            />
          </div>
        </div>

        {/* Emergency 상태 배너 */}
        <EmergencyStatusBanner />

        {/* 플로팅 탭 네비게이션 */}
        <FloatingTabNav
          panels={panelConfig}
          onToggle={togglePanel}
          onReset={resetToDefault}
        />

        {/* 메인 컨텐츠 영역 - 새로운 패널 시스템 적용 */}
        <div className={`main-content ${!panelConfig.chat.visible ? 'chat-off' : ''}`}>
          {/* 대시보드 패널 - 리사이저 활성화 */}
          <ResizablePanel
            width={panelConfig.dashboard.width}
            minWidth={panelConfig.dashboard.minWidth}
            maxWidth={panelConfig.dashboard.maxWidth}
            visible={panelConfig.dashboard.visible}
            onResize={(width) => {
              resizePanel('dashboard', width);
            }}
            onDragStart={() => startDrag('dashboard')}
            onDragEnd={endDrag}
            resizeHandle="right"
            className="dashboard-panel"
            isChatOff={!panelConfig.chat.visible}
          >
                          <Dashboard />
          </ResizablePanel>

          {/* 폼 탭 패널 - 강제 숨김 옵션 적용 */}
          {!FORCE_HIDE_FORMS_PANEL && (
            <ResizablePanel
              width={panelConfig.forms.width}
              minWidth={panelConfig.forms.minWidth}
              maxWidth={panelConfig.forms.maxWidth}
              visible={panelConfig.forms.visible}
              className="forms-panel"
            >
              <FormTabs
                onFormSelect={handleFormSelect}
                selectedFormId={selectedForm?.form_id}
                tenantId={headerCurrentTenant}
              />
            </ResizablePanel>
          )}

          {/* 채팅 패널 - 유연한 크기 (남은 공간 모두 차지) */}
          {panelConfig.chat.visible && (
            <div className={`chat-panel ${isDeveloperMode ? 'developer-mode' : ''}`}>
            {/* 개발자 모드 토글 및 Max Intelligence 토글, 새 대화 버튼 */}
            <div className="dev-mode-toggle">
              <div className="dev-mode-toggle-group">
                <label className="switch">
                  <input 
                    type="checkbox" 
                    checked={isDeveloperMode} 
                    onChange={handleDeveloperModeToggle}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">개발자 모드</span>
                
                <label 
                  className="switch max-intelligence"
                  title="더 똑똑한 모델을 사용하지만 토큰을 더 많이 소모합니다. 복잡한 분석이나 정교한 답변이 필요할 때 사용하세요."
                >
                  <input 
                    type="checkbox" 
                    checked={isMaxIntelligence} 
                    onChange={() => setIsMaxIntelligence(prev => !prev)}
                    disabled={loading}
                  />
                  <span className="slider round"></span>
                </label>
                <span 
                  className="toggle-label"
                  title="🧠 Max Intelligence 모드 (Claude Sonnet 4.0)&#10;&#10;✨ 더 똑똑하고 정교한 답변 제공&#10;💰 토큰 소모량: 일반 모드 대비 약 3-4배&#10;🎯 권장 사용: 복잡한 분석, 코딩, 창작 작업&#10;⚡ 일반적인 질문은 기본 모드를 사용하세요"
                >
                  🧠 Max Intelligence
                </span>
              </div>

              {/* 새 대화 시작 버튼 */}
              <button 
                onClick={createNewSession} 
                className="new-chat-button"
                disabled={loading}
                title="새로운 대화 세션을 시작합니다"
              >
                새 대화 시작
              </button>
            </div>

            

            
            {/* 대화 내용 표시 영역 - 스크롤 가능하도록 수정 */}
            <div className="chat-area" ref={chatAreaRef}> 
              {chatHistory.length === 0 && (
                <div className="text-center text-gray-400 py-4">
                  새로운 대화를 시작해보세요!
                </div>
              )}
              
              {/* 채팅 메시지 - ChatMessage 컴포넌트 사용 */}
              {chatHistory.map((msg, index) => (
                <ChatMessage 
                  key={`msg-${index}`} 
                  msg={msg}
                  formatMarkdownLike={formatMarkdownLike}
                  isDeveloperMode={isDeveloperMode}
                  onImageClick={handleImageClick}
                />
              ))}
              
              {/* 스트리밍 중인 경우 실시간 응답 표시 - StreamingResponse 컴포넌트 사용 */}
              {loading && chatHistory.length > 0 && 
                chatHistory[chatHistory.length-1].role === 'user' && (
                <StreamingResponse
                  streamingDisplayBlocks={streamingDisplayBlocks}
                  formatMarkdownLike={formatMarkdownLike}
                  isDeveloperMode={isDeveloperMode}
                />
              )}
              
              {/* 자동 스크롤을 위한 참조 요소 */}
              <div ref={messagesEndRef} />
            </div>
            
            {/* 에러 메시지 표시 */}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {/* 메시지 입력 영역 및 버튼 영역 */}
            <div className="input-container">
              {/* 업로드된 이미지 미리보기 */}
              {uploadedImages.length > 0 && (
                <div className="uploaded-images-preview">
                  {uploadedImages.map((img, index) => (
                    <div key={index} className="image-preview-item">
                      <img 
                        src={URL.createObjectURL(img.file)} 
                        alt={img.filename}
                        className="preview-thumbnail"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="remove-image-btn"
                        title="이미지 제거"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="input-area">
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={handleMessageChange}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  placeholder="질문을 입력하세요.."
                  className="chat-input"
                  rows={1}
                  style={{ resize: 'none', minHeight: '40px', maxHeight: '120px' }}
                />
                <div className="button-group">
                  {/* 이미지 업로드 버튼 */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    multiple
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading || isUploadingImage}
                    className="image-upload-button"
                    title="이미지 업로드"
                  >
                    {isUploadingImage ? (
                      <div className="loading-spinner small"></div>
                    ) : (
                      '📷'
                    )}
                  </button>
                  
                  {/* 통합된 전송/중단 버튼 */}
                  <button
                    type={loading ? "button" : "submit"}
                    onClick={loading ? handleCancel : undefined}
                    disabled={loading && isCancelling}
                    className={loading ? "cancel-button" : "send-button"}
                    title={loading ? "응답 중단" : "메시지 전송"}
                  >
                    {loading ? (
                      isCancelling ? (
                        <>
                          <div className="loading-spinner small"></div>
                          중단 신호 전송 중
                        </>
                      ) : (
                        <>
                          <div className="loading-spinner small"></div>
                          중단
                        </>
                      )
                    ) : (
                      '전송'
                    )}
                  </button>
                </div>
              </form>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Shift+Enter로 줄바꿈, Enter로 전송, Ctrl+V로 이미지 붙여넣기
                {loading && (
                  <span style={{ marginLeft: '10px', color: isCancelling ? '#ff9800' : '#ff6b6b' }}>
                    • {isCancelling ? '중단 신호를 전송했습니다. 잠시만 기다려주세요...' : '버튼을 클릭하여 응답을 중단할 수 있습니다'}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>
                AI의 응답은 부정확할 수 있습니다. 중요한 정보는 반드시 확인하세요.
              </div>
            </div>
            </div>
          )}
        </div> {/* main-content 끝 */}

        {/* FormCanvas 컴포넌트 */}
        <FormCanvas
          isOpen={isFormCanvasOpen}
          onClose={handleCloseFormCanvas}
          selectedFormId={selectedForm?.form_id}
          prefilledData={null}
                    tenantId={headerCurrentTenant}
        />

        {/* 개발자 모드 비밀번호 입력 모달 */}
        <PasswordPromptModal
          isOpen={isPasswordModalOpen}
          onClose={handleDeveloperModePasswordClose}
          onConfirm={handleDeveloperModePasswordConfirm}
          title="개발자 모드 활성화"
          message="개발자 모드를 활성화하려면 비밀번호를 입력하세요."
          placeholder="비밀번호 입력"
          confirmText="활성화"
          cancelText="취소"
        />

        {/* Emergency Overlay */}
        <EmergencyOverlay />

        {/* 이미지 모달 */}
        <ImageModal
          isOpen={isImageModalOpen}
          onClose={handleCloseImageModal}
          imageData={selectedImage}
        />
        
        {/* 대시보드 팝업 매니저 */}
        <DashboardPopupManager />

        {/* 전역 변수 디버그 패널 */}
        <GlobalVariablesDebugPanel />
      </div>
          </DashboardPopupProvider>
        </GlobalVariablesProvider>
      </EmergencyProvider>
    </DashboardProvider>
  );
}

export default App;
